import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Edit, Trash2, Send, Pause, Play, Mail } from 'lucide-react';
import {
  useSubscription,
  useDeliveryHistory,
  useDeleteSubscription,
  useSendSubscriptionNow,
  usePauseSubscription,
  useResumeSubscription,
} from '@/hooks/reporting/useSubscriptions';
import DeliveryHistoryTable from '@/components/reports/tables/DeliveryHistoryTable';
import ConfirmDeleteModal from '@/components/reports/modals/ConfirmDeleteModal';
import EmptyState from '@/components/reports/common/EmptyState';
import type { DeliveryHistoryQuery } from '@/types/reporting-v2';

export default function SubscriptionDetail() {
  const { subscriptionId } = useParams<{ subscriptionId: string }>();
  const navigate = useNavigate();

  const { data: subscription, isLoading, error, refetch } = useSubscription(subscriptionId);
  const deleteSubscription = useDeleteSubscription();
  const sendSubscriptionNow = useSendSubscriptionNow();
  const pauseSubscription = usePauseSubscription();
  const resumeSubscription = useResumeSubscription();

  const [historyQuery, setHistoryQuery] = useState<DeliveryHistoryQuery>({
    page: 1,
    limit: 10,
  });

  const { data: history, isLoading: loadingHistory } = useDeliveryHistory(
    subscriptionId,
    historyQuery
  );

  const [deleteModal, setDeleteModal] = useState(false);

  const handleSendNow = async () => {
    await sendSubscriptionNow.mutateAsync({ subscriptionId: subscriptionId! });
    refetch();
  };

  const handlePauseResume = async () => {
    if (subscription?.is_active) {
      await pauseSubscription.mutateAsync(subscriptionId!);
    } else {
      await resumeSubscription.mutateAsync(subscriptionId!);
    }
    refetch();
  };

  const handleDelete = async () => {
    await deleteSubscription.mutateAsync(subscriptionId!);
    navigate('/admin/reporting/subscriptions');
  };

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <h3 className="text-red-800 font-medium">Error loading subscription</h3>
          <p className="text-red-600 text-sm mt-1">{(error as Error).message}</p>
          <button
            onClick={() => navigate('/admin/reporting/subscriptions')}
            className="mt-3 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 text-sm"
          >
            Back to Subscriptions
          </button>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-gray-200 rounded w-1/3" />
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 space-y-4">
            <div className="h-4 bg-gray-200 rounded w-1/2" />
            <div className="h-4 bg-gray-200 rounded w-2/3" />
          </div>
        </div>
      </div>
    );
  }

  if (!subscription) {
    return (
      <div className="p-6">
        <EmptyState
          icon={Mail}
          title="Subscription not found"
          description="The subscription you're looking for doesn't exist or has been deleted."
          action={{
            label: 'Back to Subscriptions',
            onClick: () => navigate('/admin/reporting/subscriptions'),
          }}
        />
      </div>
    );
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  const formatReportType = (type: string) => {
    return type
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/admin/reporting/subscriptions')}
            className="p-2 hover:bg-gray-100 rounded-md transition-colors"
          >
            <ArrowLeft className="h-5 w-5 text-gray-600" />
          </button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-gray-900">{subscription.subscription_name}</h1>
              <span
                className={`px-2 py-1 text-xs font-medium rounded-full ${
                  subscription.is_active
                    ? 'bg-green-100 text-green-800'
                    : 'bg-gray-100 text-gray-800'
                }`}
              >
                {subscription.is_active ? 'Active' : 'Paused'}
              </span>
            </div>
            {subscription.description && (
              <p className="text-sm text-gray-500 mt-1">{subscription.description}</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={handleSendNow}
            disabled={sendSubscriptionNow.isPending}
            className="px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-md hover:bg-blue-100 disabled:opacity-50 flex items-center gap-2"
          >
            <Send className="h-4 w-4" />
            Send Now
          </button>
          <button
            onClick={handlePauseResume}
            disabled={pauseSubscription.isPending || resumeSubscription.isPending}
            className="px-4 py-2 text-sm font-medium text-gray-600 bg-gray-50 rounded-md hover:bg-gray-100 disabled:opacity-50 flex items-center gap-2"
          >
            {subscription.is_active ? (
              <>
                <Pause className="h-4 w-4" />
                Pause
              </>
            ) : (
              <>
                <Play className="h-4 w-4" />
                Resume
              </>
            )}
          </button>
          <button
            onClick={() => navigate(`/admin/reporting/subscriptions/${subscriptionId}/edit`)}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 flex items-center gap-2"
          >
            <Edit className="h-4 w-4" />
            Edit
          </button>
          <button
            onClick={() => setDeleteModal(true)}
            className="px-4 py-2 text-sm font-medium text-red-600 bg-red-50 rounded-md hover:bg-red-100 flex items-center gap-2"
          >
            <Trash2 className="h-4 w-4" />
            Delete
          </button>
        </div>
      </div>

      {/* Subscription Details */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Subscription Details</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <div className="text-xs text-gray-500">Report Type</div>
            <div className="text-sm font-medium text-gray-900 mt-1">
              {formatReportType(subscription.report_type)}
            </div>
          </div>
          <div>
            <div className="text-xs text-gray-500">Frequency</div>
            <div className="text-sm font-medium text-gray-900 mt-1 capitalize">
              {subscription.frequency}
            </div>
          </div>
          <div>
            <div className="text-xs text-gray-500">Next Delivery</div>
            <div className="text-sm font-medium text-gray-900 mt-1">
              {subscription.next_delivery ? formatDate(subscription.next_delivery) : 'N/A'}
            </div>
          </div>
          <div>
            <div className="text-xs text-gray-500">Last Delivery</div>
            <div className="text-sm font-medium text-gray-900 mt-1">
              {subscription.last_delivery ? formatDate(subscription.last_delivery) : 'Never'}
            </div>
          </div>
        </div>
      </div>

      {/* Recipients */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          Recipients ({subscription.recipients?.length || 0})
        </h2>
        <div className="flex flex-wrap gap-2">
          {subscription.recipients?.map((email) => (
            <span
              key={email}
              className="inline-flex items-center px-3 py-1 bg-blue-100 text-blue-800 rounded-md text-sm"
            >
              {email}
            </span>
          ))}
        </div>
      </div>

      {/* Delivery Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="text-sm text-gray-600">Total Deliveries</div>
          <div className="text-2xl font-bold text-gray-900 mt-2">
            {subscription.delivery_stats.total_deliveries}
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="text-sm text-gray-600">Successful</div>
          <div className="text-2xl font-bold text-green-600 mt-2">
            {subscription.delivery_stats.successful_deliveries}
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="text-sm text-gray-600">Failed</div>
          <div className="text-2xl font-bold text-red-600 mt-2">
            {subscription.delivery_stats.failed_deliveries}
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="text-sm text-gray-600">Success Rate</div>
          <div className="text-2xl font-bold text-blue-600 mt-2">
            {subscription.delivery_stats.success_rate}%
          </div>
        </div>
      </div>

      {/* Delivery History */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Delivery History</h2>
        <DeliveryHistoryTable
          deliveries={history?.deliveries || []}
          loading={loadingHistory}
          onViewReport={(snapshotId) => navigate(`/reports/${snapshotId}`)}
        />
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmDeleteModal
        isOpen={deleteModal}
        onClose={() => setDeleteModal(false)}
        onConfirm={handleDelete}
        title="Delete Subscription"
        message="Are you sure you want to delete this subscription? This action cannot be undone."
        itemName={subscription.subscription_name}
        loading={deleteSubscription.isPending}
      />
    </div>
  );
}
